import { PropsWithChildren } from "react";

import NextImage from "next/legacy/image";

import { Link } from "@chakra-ui/next-js";
import { Box, Center, Container, Flex, Grid, Heading, SimpleGrid, Spacer, Text } from "@chakra-ui/react";

import { Product } from "@/__generated__/graphql";
import Logo from "@/components/shared/Logo";
import useReadySearchParams from "@/hooks/useReadySearchParams";
import { pathTo } from "@/utils/pathGenerators";

const rightPanelContent = {
  environmentsAsAService: {
    title: "Environments as a Service",
    message:
      "Connect your applications' repositories to Release, and review the auto-generated, environment definition template.",
    image: "/images/environments-as-a-service.png",
  },
  deeplyIntegrated: {
    title: "Deeply Integrated into Software Development Life Cycles",
    message:
      "Release integrates deeply into software development life cycles by offering native support for GitHub, Bitbucket, Slack, email, and more.",
    image: "/images/deeply-integrated-software.png",
  },
  releaseAi: {
    title: <Logo height="44px" product={Product.ReleaseAi} />,
    message: "Smarter than your average bear",
    image: "/images/aws-billing.gif",
  },
};

type RightPanel = keyof typeof rightPanelContent;

function LoginTwoPanelLayout({
  children,
  secondaryAction,
  rightPanel = "deeplyIntegrated",
  hideRightPanel = false,
}: PropsWithChildren<{ secondaryAction?: JSX.Element; rightPanel?: RightPanel; hideRightPanel?: boolean }>) {
  const { next } = useReadySearchParams();
  const signUp = (
    <>
      Don&apos;t have an account?{" "}
      <Link fontWeight="bold" href={pathTo("/register", { search: { ...(next && { next }) } })}>
        Sign Up
      </Link>
    </>
  );

  return (
    <SimpleGrid columns={[1, 1, 1, 1, hideRightPanel ? 1 : 2]} height="100vh" spacing={0}>
      <Grid gridTemplateColumns={"1fr"} gridTemplateRows={"auto 1fr auto"} p={10}>
        <Flex flexDirection={{ base: "column", sm: "row" }}>
          <Logo h="33px" product={Product.Release} />
          <Spacer />
          <Text display={{ base: "block", xl: hideRightPanel ? "block" : "none" }} textAlign="center">
            {secondaryAction || signUp}
          </Text>
        </Flex>
        <Flex alignItems="center" display="flex" justifyContent="center">
          {children}
        </Flex>
        <Container maxW="md" textAlign="center">
          Logging in signifies that you have read and agree to the{" "}
          <Link
            fontWeight="bold"
            href="https://release.com/terms-of-service"
            rel="noopener noreferrer"
            target="_blank"
          >
            Terms of Service
          </Link>{" "}
          and our{" "}
          <Link
            fontWeight="bold"
            href="https://release.com/privacy-policy"
            rel="noopener noreferrer"
            target="_blank"
          >
            Privacy Policy
          </Link>
          .
        </Container>
      </Grid>
      {!hideRightPanel && (
        <Grid
          bg="background.darker"
          display={{ base: "none", xl: "grid" }}
          gridTemplateColumns={"1fr"}
          gridTemplateRows={"auto 1fr"}
          p={10}
        >
          <Text textAlign="end">{secondaryAction || signUp}</Text>
          <Center flexDirection="column">
            <Box h="428px" position="relative" w="100%">
              <NextImage
                priority
                alt="demonstration of Release environments"
                layout="fill"
                objectFit="contain"
                src={rightPanelContent[rightPanel].image}
              />
            </Box>
            <Container maxW="lg" textAlign="center">
              <Heading
                alignItems="center"
                color="text.darkest"
                display="flex"
                justifyContent="center"
                lineHeight="44px"
                my={3}
                size="lg"
              >
                {rightPanelContent[rightPanel].title}
              </Heading>
              <Text>{rightPanelContent[rightPanel].message}</Text>
            </Container>
          </Center>
        </Grid>
      )}
    </SimpleGrid>
  );
}

export default LoginTwoPanelLayout;
